import { useState, useEffect, useMemo } from "react";
import { clientAccountValidation } from "shared/utils/validation";
import { useFormik } from "formik";
import { UpdateUserService, GetUserProfileService } from "shared/services/user";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { useSelector, useDispatch } from "react-redux";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import ProfileUpload from "shared/components/common/customProfileUpload";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomInput from "shared/components/common/customInput";
import CustomAutoComplete from "shared/components/common/customAutoComplete";
import NotificationPreference from "shared/components/common/customNotificationPreference";
import ChangePasswordSection from "shared/components/common/changePassword";
import CustomButton from "shared/components/common/customButton";
import MobileLogoutButton from "shared/components/dashboardCommon/logoutButton";

const ClientAccountSetting = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.root.auth);
  const user = useMemo(() => auth?.user || {}, [auth?.user]);

  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [deleteProfileAsset, setDeleteProfileAsset] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    id: user.client_id || user.user_id || "",
    displayName: user.display_name || "",
    currentAddress: user.current_address || "",
    billingAddress: user.billing_address || "",
    receiveEmailNotification: user.receive_email_notification || false,
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await GetUserProfileService();

        if (response?.data) {
          const profileData = response.data.data || response.data;

          const normalizedProfile = { ...profileData };

          if ("receive_email_notifications" in normalizedProfile) {
            normalizedProfile.receive_email_notification =
              normalizedProfile.receive_email_notifications;
            delete normalizedProfile.receive_email_notifications;
          }

          const updatedUser = {
            ...user,
            ...normalizedProfile,
          };

          delete updatedUser.receive_email_notifications;

          dispatch(
            setAuthReducer({
              user: updatedUser,
            }),
          );
        }
      } catch (error) {
        handleErrors(error);
      }
    };

    fetchUserProfile();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) {
      const clientId = user.client_id || user.user_id || "";

      setFormData({
        id: clientId,
        displayName: user.display_name || "",
        currentAddress: user.current_address || "",
        billingAddress: user.billing_address || "",
        receiveEmailNotification: user.receive_email_notification || false,
      });
      const profileImageUrl =
        user.profile_asset?.path || user.path || user.user_profile_asset?.path;
      if (profileImageUrl) {
        setPreview(profileImageUrl);
      }
    }
  }, [user]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: formData.id,
      displayName: formData.displayName,
      currentAddress: formData.currentAddress,
      billingAddress: formData.billingAddress,
      receiveEmailNotification: formData.receiveEmailNotification,
    },
    validationSchema: clientAccountValidation,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        let payload: any;
        if (profileFile || deleteProfileAsset) {
          payload = new FormData();
          payload.append("display_name", values.displayName);
          payload.append(
            "receive_email_notification",
            values.receiveEmailNotification.toString(),
          );

          if (values.currentAddress) {
            payload.append("current_address", values.currentAddress);
          }
          if (values.billingAddress) {
            payload.append("billing_address", values.billingAddress);
          }

          if (profileFile) {
            payload.append("profile_asset", profileFile);
          }

          if (deleteProfileAsset) {
            payload.append("delete_profile_asset", "true");
          }
        } else {
          payload = {
            display_name: values.displayName,
            receive_email_notification: values.receiveEmailNotification,
          };

          if (values.currentAddress) {
            payload.current_address = values.currentAddress;
          }
          if (values.billingAddress) {
            payload.billing_address = values.billingAddress;
          }
        }

        const response = await UpdateUserService(payload);

        if (response.status === 200) {
          const message =
            response.data?.message ||
            response.data?.data?.message ||
            "Profile updated successfully";
          toastMessage("success", message);
          setFormData(values);

          const hadProfileFile = !!profileFile;
          const hadDeleteRequest = deleteProfileAsset;

          const updatedUser = {
            ...user,
            display_name: values.displayName,
            current_address: values.currentAddress,
            billing_address: values.billingAddress,
            receive_email_notification: values.receiveEmailNotification,
          };

          if (hadDeleteRequest) {
            updatedUser.profile_asset = null;
            updatedUser.path = null;
            setPreview(null);
          } else if (hadProfileFile) {
            const newProfileAssetPath =
              response.data?.profile_asset ||
              response.data?.profile_asset?.path ||
              response.data?.path ||
              response.data?.data?.profile_asset ||
              response.data?.data?.path;

            if (newProfileAssetPath) {
              updatedUser.profile_asset = {
                ...user.profile_asset,
                path: newProfileAssetPath,
              };
              updatedUser.path = newProfileAssetPath;
              setPreview(newProfileAssetPath);
            }
          }

          dispatch(setAuthReducer({ user: updatedUser }));

          setProfileFile(null);
          setDeleteProfileAsset(false);
        }
      } catch (error: any) {
        handleErrors(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors: formikErrors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
  } = formik;

  const handleRemoveProfilePicture = () => {
    setProfileFile(null);
    setPreview(null);
    setDeleteProfileAsset(true);
  };

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        "flex flex-col md:gap-5 sm:gap-4 gap-3 w-full h-full",
      )}
    >
      <CustomPageHeading
        title="Profile & Settings"
        description="Tailor your creative setup"
      />

      <div className={classNames(styles.contentEnclosure, "grid")}>
        <ProfileUpload
          title={user.display_name}
          description={
            user.user_type
              ? user.user_type
                  .split("_")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(" ")
              : ""
          }
          defaultPreview={preview}
          onChange={(file, url) => {
            setProfileFile(file);
            setPreview(url);
            setDeleteProfileAsset(false);
          }}
          onRemove={handleRemoveProfilePicture}
        />

        <form onSubmit={handleSubmit}>
          <div className={classNames(styles.alignmentEnclosure)}>
            <div className={classNames(styles.mainInput)}>
              <div className={classNames(styles.inputEnclosure, "flex")}>
                <CustomInput
                  label="Display Name"
                  required
                  placeholder="Anderson Smith"
                  name="displayName"
                  value={values.displayName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.displayName ? formikErrors.displayName : ""}
                  customInputContainer={styles.inputContainer}
                  notRequiredMargin={true}
                />

                <CustomInput
                  label="Email"
                  placeholder="andersonsmith@gmail.com"
                  value={user.email || ""}
                  readOnly
                  customInputContainer={styles.inputContainer}
                  notRequiredMargin={true}
                />

                <CustomInput
                  label="Phone Number"
                  placeholder="(555) 123-4567"
                  value={user.phone || ""}
                  readOnly
                  customInputContainer={styles.inputContainer}
                  notRequiredMargin={true}
                />
              </div>

              <div className={classNames(styles.inputEnclosure, "flex")}>
                <CustomAutoComplete
                  label="Current Address"
                  placeholder="Enter street address"
                  formikKey="currentAddress"
                  value={values.currentAddress}
                  setFieldValue={formik.setFieldValue}
                  error={
                    touched.currentAddress
                      ? (formikErrors.currentAddress as string)
                      : ""
                  }
                  customInputContainer={styles.inputContainer}
                  notRequiredMargin={true}
                />

                <CustomAutoComplete
                  label="Billing Address"
                  placeholder="Enter street address"
                  formikKey="billingAddress"
                  value={values.billingAddress}
                  setFieldValue={formik.setFieldValue}
                  error={
                    touched.billingAddress
                      ? (formikErrors.billingAddress as string)
                      : ""
                  }
                  customInputContainer={styles.inputContainer}
                  notRequiredMargin={true}
                />
              </div>
            </div>

            <div className={classNames(styles.buttonEnclosure)}>
              <CustomButton
                title="Save Changes"
                containerStyle={classNames(styles.submitButton)}
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || !isValid}
              />
            </div>
          </div>
        </form>
      </div>

      <ChangePasswordSection />

      <NotificationPreference
        icon={<Icons.EditState />}
        emailEnabled={values.receiveEmailNotification}
        onEmailToggleChange={(checked: boolean) => {
          formik.setFieldValue("receiveEmailNotification", checked);
        }}
      />

      <MobileLogoutButton />
    </div>
  );
};

export default ClientAccountSetting;
